import {
  Box,
  Button,
  Collapse,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useBoolean,
  useDisclosure,
  useTheme,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import Head from "next/head";
import { useRef, useState } from "react";
import { AxiosService } from "src/axios";
import { FormError } from "src/components/shared/FormError";
import { FormInput, requiredMsg } from "src/components/shared/FormInput";
import { errorOccurred, strings, titlePostfix, urls } from "src/constants";
import { useAuth } from "src/hooks/useAuth";
import { Sentry } from "src/sentry";
import * as Yup from "yup";
import { useRouter } from "next/router";
import { getUrlParam } from "src/hooks/useRenderWithFormData";

export function Login(props: {
  defaultNextPath: string;
  showGoogleSSO?: boolean;
  showSignup?: boolean;
  showPasswordReset?: boolean;
}) {
  const theme = useTheme();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const auth = useAuth();
  const router = useRouter();

  const goToNextPage = async () => {
    const next_path = getUrlParam("next_path") ?? props.defaultNextPath;
    return router.push(next_path);
  };

  const handleSubmit = async (data: { email: string; password: string }) => {
    try {
      setErrorMessage(null);
      await auth.login(data);
      return goToNextPage();
    } catch (error: any) {
      if (error.code === "credentials") {
        setErrorMessage("Incorrect email or password");
      } else {
        setErrorMessage(errorOccurred);
        Sentry.captureException(error);
      }
    }
  };

  return (
    <Box mx="auto" w="100%">
      <Head>
        <title>Log In{titlePostfix}</title>
      </Head>

      <Heading as="h1" variant="h1">
        Log In
      </Heading>

      <Formik
        initialValues={{
          email: getUrlParam("email") ?? "",
          password: "",
        }}
        validationSchema={Yup.object().shape({
          email: Yup.string().email("Enter a valid email").required(requiredMsg),
          password: Yup.string().required(requiredMsg),
        })}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <FormInput name="email" label="Email" placeholder="name@email.com" />
            <FormInput
              name="password"
              type="password"
              label="Password"
              placeholder="●●●●●●●●●●●●●●"
            />

            <FormError isVisible={errorMessage} mb={theme.space.md}>
              {errorMessage}
            </FormError>

            <Button
              type="submit"
              isLoading={isSubmitting}
              isFullWidth
              my={theme.space.md}
              variant="solid"
            >
              LOGIN
            </Button>

            {/* {props.showGoogleSSO && <GoogleSSO />} */}
          </Form>
        )}
      </Formik>

      {props.showPasswordReset && <PasswordResetModal />}
    </Box>
  );
}

function PasswordResetModal() {
  const theme = useTheme();
  const [email, setEmail] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useBoolean();
  const [isSuccess, setIsSuccess] = useBoolean();
  const [isError, setIsError] = useBoolean();

  const modalControl = useDisclosure();

  const initialRef = useRef() as any;
  const finalRef = useRef() as any;

  async function resetPassword() {
    setIsSubmitting.on();
    setIsError.off();
    setIsSuccess.off();
    try {
      await AxiosService.post(`users/password-reset/`, { email });
      setIsSuccess.on();
    } catch (error: any) {
      setIsError.on();
      if (error?.http_status !== 404) {
        Sentry.captureException(error);
      }
    }
    setIsSubmitting.off();
  }

  return (
    <>
      <Text
        mt={theme.space.md}
        textStyle="label"
        fontWeight="normal"
        color={theme.colors.momentum.grey["900"]}
      >
        Forgot your password?
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <Link
          onClick={modalControl.onOpen}
          px={theme.space.xs}
          variant="primary"
          fontWeight="bold"
        >
          Reset Password
        </Link>
      </Text>

      <Modal
        initialFocusRef={initialRef}
        finalFocusRef={finalRef}
        isOpen={modalControl.isOpen}
        onClose={modalControl.onClose}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Reset Password</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>Email</FormLabel>
              <Input
                ref={initialRef}
                name="email"
                placeholder="Email"
                onChange={(event) => setEmail(event.target.value)}
              />
            </FormControl>

            <FormError isVisible={isError} mt={theme.space.sm}>
              Please check that your email is correct or contact support at{" "}
              {strings.supportEmail}
            </FormError>

            <Collapse in={isSuccess}>
              <Text mt={theme.space.sm} color="green.500" fontSize="sm">
                Please use the password reset link that was sent to your email
              </Text>
            </Collapse>
          </ModalBody>

          <ModalFooter>
            <Button isLoading={isSubmitting} onClick={() => resetPassword()}>
              Reset
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
